import { BRAND } from "@/lib/brand";
import { LogoMark, Wordmark, Logo } from "@/components/Logo";
import { Palette, Type, Sparkles, ImageDown, ShieldCheck } from "lucide-react";

const swatches = [
  ["Deep Navy", BRAND.colors.navy, "Primary — premium surfaces, headers, text"],
  ["Navy Light", BRAND.colors.navyLight, "Elevated navy surfaces / cards"],
  ["Teal", BRAND.colors.teal, "Brand accent — CTAs, links, highlights"],
  ["Teal Hover", BRAND.colors.tealHover, "Accent hover / pressed"],
  ["Orange", BRAND.colors.orange, "Controlled accent — use sparingly"],
  ["Success Green", BRAND.colors.success, "Success / paid / positive only"],
  ["Error Red", BRAND.colors.error, "Errors / failed / overdue only"],
];

function AssetCard({ title, note, bg, children }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className={`flex min-h-[120px] items-center justify-center p-6 ${bg}`}>{children}</div>
      <div className="border-t border-slate-100 p-4"><p className="font-semibold text-[#0B132B]">{title}</p><p className="text-xs text-slate-500">{note}</p></div>
    </div>
  );
}

export default function AdminBrandKit() {
  const files = [
    ["Primary Logo (PNG)", BRAND.assets.logoPrimary],
    ["Dark Background (PNG)", BRAND.assets.logoDark],
    ["Light Background (PNG)", BRAND.assets.logoLight],
    ["Icon (PNG)", BRAND.assets.icon],
    ["Logo (SVG)", BRAND.assets.logoSvg],
    ["Favicon", BRAND.assets.favicon],
    ["App Icon", BRAND.assets.appIcon],
  ];
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">Brand Kit</h1><p className="text-sm text-slate-500">Internal reference for the {BRAND.name} identity — {BRAND.tagline}.</p></div>
        <span className="inline-flex items-center gap-1.5 rounded-md bg-[#0B132B] px-3 py-1.5 text-xs font-semibold text-[#14B8A6]"><ShieldCheck size={14} /> Internal only</span>
      </div>

      {/* Logo variants */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-[#0B132B]"><Sparkles size={18} className="text-[#14B8A6]" /> Logo System</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <AssetCard title="Primary (light bg)" note="Default lockup on white/light surfaces" bg="bg-white"><Logo to="/admin/brand-kit" showTagline /></AssetCard>
          <AssetCard title="On dark navy" note="Use on deep navy premium areas" bg="navy-grain"><Logo to="/admin/brand-kit" light showTagline /></AssetCard>
          <AssetCard title="Icon-only mark" note="Compact spaces, favicon, app icon" bg="bg-slate-50"><LogoMark size={72} className="rounded-2xl shadow" /></AssetCard>
          <AssetCard title="Wordmark (light)" note="Text-only, light background" bg="bg-white"><Wordmark /></AssetCard>
          <AssetCard title="Wordmark (dark)" note="Text-only, dark background" bg="navy-grain"><Wordmark light /></AssetCard>
          <AssetCard title="Primary PNG asset" note="Approved raster export" bg="bg-white"><img src={BRAND.assets.logoPrimary} alt="ZRo Pe primary logo" className="max-h-16 object-contain" /></AssetCard>
        </div>
      </div>

      {/* Downloadable assets */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-[#0B132B]"><ImageDown size={18} className="text-[#14B8A6]" /> Asset Files</h2>
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          {files.map(([name, href]) => (
            <div key={name} className="flex items-center justify-between border-b border-slate-100 px-5 py-3 last:border-0">
              <span className="text-sm font-medium text-[#0B132B]">{name}</span>
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-slate-400">{href}</span>
                <a href={href} download target="_blank" rel="noreferrer" data-testid={`brand-dl-${name.replace(/\W+/g, "-").toLowerCase()}`} className="text-sm font-semibold text-[#14B8A6] hover:underline">Download</a>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Colors */}
      <div>
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-[#0B132B]"><Palette size={18} className="text-[#14B8A6]" /> Brand Colors</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {swatches.map(([name, hex, note]) => (
            <div key={name} className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="h-16 w-full" style={{ background: hex }} />
              <div className="p-3"><p className="text-sm font-semibold text-[#0B132B]">{name}</p><p className="font-mono text-xs text-slate-500">{hex}</p><p className="mt-1 text-xs text-slate-400">{note}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* Typography + usage */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#0B132B]"><Type size={18} className="text-[#14B8A6]" /> Typography</h2>
          <p className="mt-3 font-display text-3xl font-extrabold text-[#0B132B]">Cabinet Grotesk / Satoshi</p>
          <p className="text-xs text-slate-500">Display & headings</p>
          <p className="mt-3 text-base text-[#0B132B]">Inter — body & UI text</p>
          <p className="text-xs text-slate-500">Clean, professional, high readability at small sizes.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold text-[#0B132B]">Usage Notes</h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li>• Tagline "{BRAND.tagline}" — use in marketing, auth, footer, PDF. Not in compact headers.</li>
            <li>• Keep ~80–90% professional fintech; mascots/illustrations only in rewards, offers, onboarding, empty states.</li>
            <li>• Orange is a controlled accent — never let it dominate.</li>
            <li>• Green only for success/paid/progress; red only for errors/overdue.</li>
            <li>• No unsupported claims (RBI approved, 100% secure, guaranteed savings, etc.).</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
